import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/auth-store";
import { LoadingSpinner } from "../components/UI/LoadingSpinner";
import CompanyAdminProhibited from "../components/CompanyAdmin/CompanyAdminProhibited";
import Pod24AdminPRohibited from "../components/Pod24Admin/Pod24AdminProhibited";

interface ProtectedRouteProps {
  children: React.ReactNode;
  accessType?: AccessType;
}

export enum AccessType {
  DEFAULT,
  MANAGER,
  COMPANY_ADMIN,
  ADMIN,
}

export const RoleProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  accessType = AccessType.DEFAULT,
}) => {
  const {
    isAuthenticated,
    user,
    isLoading,
    refreshToken,
    canAccessCompany,
    isAdmin,
  } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    // 토큰이 있지만 사용자 정보가 없는 경우 토큰 갱신 시도
    if (isAuthenticated && !user && !isLoading) {
      refreshToken().catch(() => {
        // 토큰 갱신 실패는 store에서 자동으로 로그아웃 처리됨
      });
    }
  }, [isAuthenticated, user, isLoading, refreshToken]);

  // 로딩 중일 때
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // 회사 관리 영역(MES) — COMPANY_ADMIN 또는 COMPANY_MANAGER 허용
  if (isAuthenticated && accessType === AccessType.COMPANY_ADMIN) {
    if (!canAccessCompany()) {
      return <CompanyAdminProhibited />;
    }
  }

  // POD24 관리자
  if (isAuthenticated && accessType === AccessType.ADMIN) {
    if (!isAdmin()) {
      return <Pod24AdminPRohibited />;
    }
  }

  // 인증되지 않은 경우
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 이메일 미인증 사용자는 이메일 인증 페이지로 리다이렉트
  // 단, 이메일 인증 관련 페이지는 예외 처리
  const isEmailVerificationPage = location.pathname.startsWith("/auth/email");
  if (user.emailVerified === false && !isEmailVerificationPage) {
    return <Navigate to="/auth/email-verification" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
